import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: '🪴 Integrating Environmental Well-Being into Holistic Life Balance',
    content: [
      '<strong>Learning Goal:</strong> Integrate environmental wellness into a balanced lifestyle, strengthen the relationship between environment, health, and purpose, and commit to sustainable and mindful living practices.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• How environment interacts with other wellness domains',
      '• The role of gratitude and mindfulness in appreciating surroundings',
      '• Living intentionally within one\'s environment',
      '• Creating a Well Environment Action Plan',
      '<br/>',
      '<strong>Discussion:</strong> In this final module, participants integrate environmental well-being with other SOWBEE domains (physical, emotional, social, and spiritual). Environmental wellness isn\'t separate from other aspects of health – it\'s deeply interconnected with all areas of well-being.'
    ]
  },
  {
    title: 'Environmental Wellness and Other Life Domains',
    content: [
      'Environmental well-being interacts with and influences all other aspects of health and wellness.',
      '<br/>',
      '<strong>Physical Health Connections:</strong>',
      '• Clean environments support immune function and reduce chronic diseases',
      '• Safe spaces encourage physical activity and outdoor recreation',
      '• Access to healthy food options improves nutrition',
      '• Restorative environments enhance sleep quality and recovery',
      '<br/>',
      '<strong>Mental Health Integration:</strong>',
      '• Supportive environments reduce stress and anxiety',
      '• Natural surroundings improve mood and cognitive function',
      '• Safe communities build feelings of security and belonging',
      '• Environmental control enhances feelings of agency and empowerment',
      '<br/>',
      '<strong>Social Well-Being Links:</strong>',
      '• Community spaces facilitate social connections and relationships',
      '• Shared environmental stewardship builds community bonds',
      '• Inclusive environments promote social equity and justice',
      '• Public spaces encourage social interaction and cultural exchange',
      '<br/>',
      '<strong>Spiritual Connections:</strong>',
      '• Nature experiences foster awe, wonder, and spiritual connection',
      '• Environmental stewardship provides purpose and meaning',
      '• Sustainable living aligns with many spiritual values',
      '• Mindful interaction with environment enhances present-moment awareness',
      '<br/>',
      '<strong>Holistic Impact:</strong> Poor environmental conditions can undermine all other wellness efforts. Conversely, supportive environments amplify the benefits of other wellness practices.'
    ]
  },
  {
    title: 'Gratitude and Mindfulness in Environmental Appreciation',
    content: [
      'Practicing gratitude and mindfulness enhances our relationship with our environment.',
      '<br/>',
      '<strong>Environmental Gratitude Practices:</strong>',
      '• <strong>Daily Appreciation:</strong> Notice and give thanks for environmental blessings',
      '• <strong>Seasonal Awareness:</strong> Celebrate natural cycles and changes',
      '• <strong>Resource Recognition:</strong> Acknowledge the sources of our daily necessities',
      '• <strong>Beauty Noticing:</strong> Find wonder in everyday natural surroundings',
      '<br/>',
      '<strong>Mindful Environmental Engagement:</strong>',
      '• <strong>Present-Moment Awareness:</strong> Fully experience your surroundings',
      '• <strong>Sensory Attention:</strong> Notice sights, sounds, smells, textures in nature',
      '• <strong>Non-Judgmental Observation:</strong> Accept environments as they are',
      '• <strong>Compassionate Action:</strong> Respond to environmental needs with care',
      '<br/>',
      '<strong>Benefits of Mindful Environmental Practice:</strong>',
      '• <strong>Reduced Stress:</strong> Mindfulness lowers cortisol and anxiety',
      '• <strong>Increased Satisfaction:</strong> Gratitude enhances life satisfaction',
      '• <strong>Better Relationships:</strong> Mindful presence improves social connections',
      '• <strong>Environmental Stewardship:</strong> Awareness leads to caring action',
      '<br/>',
      '<strong>Practical Applications:</strong>',
      '• Start a "gratitude for surroundings" journal',
      '• Practice "forest bathing" or mindful nature walks',
      '• Create rituals for appreciating daily environmental supports',
      '• Use environmental challenges as mindfulness opportunities'
    ]
  },
  {
    title: 'Intentional Living Within Your Environment',
    content: [
      'Living intentionally means making conscious choices about how you interact with and shape your environment.',
      '<br/>',
      '<strong>Principles of Intentional Environmental Living:</strong>',
      '• <strong>Conscious Consumption:</strong> Choose items and experiences mindfully',
      '• <strong>Minimalist Mindset:</strong> Focus on what truly adds value to life',
      '• <strong>Quality Over Quantity:</strong> Invest in durable, meaningful possessions',
      '• <strong>Seasonal Awareness:</strong> Align lifestyle with natural rhythms',
      '• <strong>Community Connection:</strong> Participate actively in local environmental care',
      '<br/>',
      '<strong>Creating Environmental Harmony:</strong>',
      '• <strong>Space Optimization:</strong> Arrange living spaces to support your values and activities',
      '• <strong>Energy Alignment:</strong> Ensure your environment matches your energy needs',
      '• <strong>Relationship Building:</strong> Foster positive connections with your surroundings',
      '• <strong>Adaptive Flexibility:</strong> Adjust environments as life circumstances change',
      '<br/>',
      '<strong>Overcoming Environmental Challenges:</strong>',
      '• <strong>Limited Control:</strong> Focus on what you can influence, accept what you cannot',
      '• <strong>Resource Constraints:</strong> Find creative solutions within your means',
      '• <strong>Systemic Issues:</strong> Combine personal action with community advocacy',
      '• <strong>Change Resistance:</strong> Start small and build sustainable habits',
      '<br/>',
      '<strong>The Art of Environmental Balance:</strong> Intentional living isn\'t about perfection – it\'s about creating environments that consistently support your health, happiness, and values.'
    ]
  },
  {
    title: 'Designing Your Well Environment Action Plan',
    content: [
      'Create a comprehensive plan for maintaining and improving your environmental well-being.',
      '<br/>',
      '<strong>Assessment Integration:</strong>',
      '• Review your WHOQOL-BREF environmental scores',
      '• Identify your top environmental strengths and challenges',
      '• Consider how environmental factors affect other wellness domains',
      '• Reflect on your sustainability pledge commitments',
      '<br/>',
      '<strong>Goal Setting Framework:</strong>',
      '• <strong>Short-term Goals (1-3 months):</strong> Immediate improvements you can implement quickly',
      '• <strong>Medium-term Goals (3-6 months):</strong> Moderate changes requiring planning',
      '• <strong>Long-term Goals (6-12 months):</strong> Significant transformations or lifestyle shifts',
      '• <strong>Ongoing Practices:</strong> Daily or weekly habits to maintain environmental wellness',
      '<br/>',
      '<strong>SMART Goal Components:</strong>',
      '• <strong>Specific:</strong> Clearly defined actions and outcomes',
      '• <strong>Measurable:</strong> Quantifiable progress indicators',
      '• <strong>Achievable:</strong> Realistic within your resources and constraints',
      '• <strong>Relevant:</strong> Aligned with your values and wellness priorities',
      '• <strong>Time-bound:</strong> Specific deadlines and review periods',
      '<br/>',
      '<strong>Implementation Strategies:</strong>',
      '• <strong>Habit Stacking:</strong> Attach new environmental habits to existing routines',
      '• <strong>Accountability Systems:</strong> Share goals with supportive friends or communities',
      '• <strong>Progress Tracking:</strong> Regular check-ins and adjustments',
      '• <strong>Reward Systems:</strong> Celebrate environmental wellness achievements',
      '<br/>',
      '<strong>Contingency Planning:</strong> Prepare for challenges and setbacks by having backup strategies and alternative approaches.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 6: Integrating Environmental Well-Being into Holistic Life Balance! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about environmental wellness integration and holistic well-being.',
        'The quiz will help you review key concepts about connecting environmental health with overall life balance.'
      ]
    }
  }
];

const EnvironmentalModule6Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('environmentalM6');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/environmental-resources');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/environmental-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left environmental-panel"
        >
          <div className="module-header">
            <h1>Module 6</h1>
            <p className="module-subtitle">Integrating Environmental Well-Being into Holistic Life Balance</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Connect environmental wellness with all aspects of your holistic well-being.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/environmental-module-6/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule6Page;

